// Meer: an overview. Tiles for what is used often, then the settings in groups under a heading. Account, passkeys,
// wissen and uitloggen live on Account en toestellen. The tiles follow what is opened on this device (golf 9).

import { useEffect, type ComponentType, type MouseEvent } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { planningDate } from "@shared/time";
import { tileOrder, type TileUsage } from "@shared/usage";
import { getMeta, setMeta } from "../lib/db";
import {
  Bell,
  BookOpen,
  CalendarHeart,
  ChevronRight,
  Gift,
  HeartHandshake,
  History,
  LayoutList,
  ListChecks,
  Moon,
  NotebookPen,
  Repeat,
  Settings,
  Shirt,
  Smartphone,
  Sunrise,
  Tags,
  UtensilsCrossed,
} from "lucide-react";
import type { Me } from "../lib/session";
import { BackupWarning } from "./MoreSub";
import { navigate } from "../lib/hooks";

type Icon = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
type Link = { to: string; label: string; icon: Icon; hint?: string };
type Group = { title: string; links: Link[] };

/** What PK uses often: open it, not set it up. Six tiles; the rest of these are listed under "Ook in Meer". */
const OWNER_TILES: Link[] = [
  { to: "/agenda", label: "Agenda", icon: CalendarHeart },
  { to: "/menu", label: "Weekmenu", icon: UtensilsCrossed },
  { to: "/samen", label: "Samen doen", icon: HeartHandshake },
  { to: "/meer/verjaardagen", label: "Verjaardagen", icon: Gift },
  { to: "/meer/notities", label: "Notities", icon: NotebookPen },
  { to: "/meer/terugblik", label: "Terugblik", icon: History },
  { to: "/recepten", label: "Recepten", icon: BookOpen },
];
const TILE_COUNT = 6;

const OWNER_GROUPS: Group[] = [
  {
    title: "Mijn dag inrichten",
    links: [
      { to: "/meer/ochtend", label: "Ochtend", icon: Sunrise, hint: "Je ochtendroutine" },
      { to: "/meer/avond", label: "Avond", icon: Moon, hint: "Je avondroutine instellen" },
      { to: "/meer/ritme", label: "Ritme", icon: LayoutList, hint: "Dagtypen en blokken" },
      { to: "/meer/klaarleggen", label: "Klaarleggen", icon: Shirt, hint: "Wat je 's avonds klaarlegt" },
    ],
  },
  {
    title: "Routines en taken",
    links: [
      { to: "/meer/routines", label: "Routines", icon: Repeat, hint: "Wat elke week terugkomt" },
      { to: "/meer/af-en-toe", label: "Af en toe", icon: ListChecks, hint: "Wat zo nu en dan moet" },
      { to: "/meer/categorieen", label: "Categorieën", icon: Tags, hint: "Soorten taken, zoals Klusjes" },
    ],
  },
  {
    title: "App",
    links: [
      { to: "/meer/instellingen", label: "Instellingen", icon: Settings, hint: "Ochtendstand, rust na bedtijd, slimme hulp, wie eet mee" },
      { to: "/meer/meldingen", label: "Meldingen", icon: Bell, hint: "Aan of uit, stille uren" },
      { to: "/meer/toestellen", label: "Account en toestellen", icon: Smartphone, hint: "Passkeys, wissen, uitloggen" },
    ],
  },
];

/** Tsjits: what the two of them share and her own things; no planner. */
const MEMBER_TILES: Link[] = [
  { to: "/samen", label: "Samen doen", icon: HeartHandshake },
  { to: "/recepten", label: "Recepten", icon: BookOpen },
  { to: "/meer/notities", label: "Notities", icon: NotebookPen },
];

const MEMBER_GROUPS: Group[] = [
  {
    title: "App",
    links: [
      { to: "/meer/meldingen", label: "Meldingen", icon: Bell, hint: "Aan of uit, stille uren" },
      { to: "/meer/toestellen", label: "Account en toestellen", icon: Smartphone, hint: "Passkeys, uitloggen" },
    ],
  },
];

const EMPTY_USAGE: TileUsage = { counts: {}, since: null, order: null, orderedOn: null };

/** Counts an open on this device; only for the tiles' order, never sent anywhere. */
async function countOpen(to: string): Promise<void> {
  try {
    const usage = (await getMeta<TileUsage>("moreUsage")) ?? EMPTY_USAGE;
    await setMeta("moreUsage", {
      ...usage,
      since: usage.since ?? planningDate(new Date()),
      counts: { ...usage.counts, [to]: (usage.counts[to] ?? 0) + 1 },
    });
  } catch {
    // Counting is a nicety; a failure never blocks opening the screen.
  }
}

function go(to: string) {
  return (e: MouseEvent) => {
    e.preventDefault();
    void countOpen(to);
    navigate(to);
  };
}

/** The owner's tiles in the order of use (the default for the first two weeks). */
function useTiles(owner: boolean): { tiles: Link[]; rest: Link[] } {
  const today = planningDate(new Date());
  const usage = useLiveQuery(async () => (await getMeta<TileUsage>("moreUsage")) ?? EMPTY_USAGE);
  const result =
    owner && usage
      ? tileOrder(
          OWNER_TILES.map((t) => t.to),
          usage,
          today,
        )
      : null;
  useEffect(() => {
    if (result?.decided && usage) void setMeta("moreUsage", { ...usage, order: result.order, orderedOn: today });
  }, [result?.decided, usage, result?.order, today]);
  if (!owner) return { tiles: MEMBER_TILES, rest: [] };
  const ordered = (result?.order ?? OWNER_TILES.map((t) => t.to)).map((to) => OWNER_TILES.find((t) => t.to === to)!);
  return { tiles: ordered.slice(0, TILE_COUNT), rest: ordered.slice(TILE_COUNT) };
}

export function MoreScreen({ me }: { me: Me }) {
  const owner = me.user.role === "owner";
  const { tiles, rest } = useTiles(owner);
  const groups = rest.length
    ? [{ title: "Andere onderdelen", links: rest }, ...(owner ? OWNER_GROUPS : MEMBER_GROUPS)]
    : owner
      ? OWNER_GROUPS
      : MEMBER_GROUPS;
  return (
    <main className="mx-auto max-w-xl px-4 pt-[max(1.25rem,env(safe-area-inset-top))] pb-44">
      <h1 className="mb-6 text-[1.75rem] font-bold">Meer</h1>
      {owner && <BackupWarning />}

      <nav aria-label="Onderdelen">
        <ul className="mb-8 grid grid-cols-3 gap-2">
          {tiles.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <a
                href={to}
                onClick={go(to)}
                className="flex min-h-22 flex-col items-center justify-center gap-1.5 rounded-2xl border border-mist bg-surface px-1 py-3 text-center"
              >
                <Icon aria-hidden className="size-6 text-pine" />
                <span className="text-sm font-semibold [overflow-wrap:anywhere]">{label}</span>
              </a>
            </li>
          ))}
        </ul>

        {groups.map((group) => {
          const id = `groep-${group.title.toLowerCase().replace(/\s+/g, "-")}`;
          return (
            <section key={group.title} aria-labelledby={id} className="mb-8">
              <h2 id={id} className="mb-2 px-1 text-sm font-semibold text-ink-soft">
                {group.title}
              </h2>
              <ul className="divide-y divide-mist rounded-2xl border border-mist bg-surface">
                {group.links.map(({ to, label, icon: Icon, hint }) => {
                  const hintId = `uitleg-${to.slice(1).replace(/\//g, "-")}`;
                  return (
                    <li key={to}>
                      <a
                        href={to}
                        onClick={go(to)}
                        aria-describedby={hint ? hintId : undefined}
                        className="flex min-h-14 items-center gap-3 px-3 py-2"
                      >
                        <Icon aria-hidden className="size-5 shrink-0 text-pine" />
                        <span className="min-w-0 flex-1">
                          <span className="block font-semibold">{label}</span>
                          {hint && (
                            <span id={hintId} aria-hidden className="block text-sm text-ink-soft">
                              {hint}
                            </span>
                          )}
                        </span>
                        <ChevronRight aria-hidden className="size-5 shrink-0 text-ink-soft" />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </nav>
    </main>
  );
}
